/**
 * Forgot Password Page
 * User enters only a username; we look up their email + mobile and let them
 * choose how to receive the reset link (Email / SMS / WhatsApp).
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAuth, type CaptchaData, type ForgotPasswordPayload } from '../../../../context/AuthContext';
import FormInput from '../../../../components/FormInput';
import AuthShell from '../components/AuthShell';
import { Skeleton } from '@/components/base/Skeleton';

const resetPasswordSchema = yup.object({
  username: yup.string().required('Username is required'),
  captcha: yup.string()
    .required('Captcha is required')
    .matches(/^\d{1,4}$/, 'Please enter a valid answer (1-4 digits)'),
});

type ResetPasswordForm = yup.InferType<typeof resetPasswordSchema>;

// Mask helpers for displaying the fetched contact details
const maskEmail = (email: string): string => {
  if (!email || !email.includes('@')) return email || '';
  const [name, domain] = email.split('@');
  const visible = name.slice(0, 1);
  return `${visible}${'*'.repeat(Math.max(name.length - 1, 1))}@${domain}`;
};

const maskMobile = (mobile: string): string => {
  const digits = String(mobile || '').replace(/\D/g, '');
  if (digits.length < 4) return digits;
  return `${'*'.repeat(digits.length - 4)}${digits.slice(-4)}`;
};

const ForgotPassword: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  // Tracks which delivery channel button is currently submitting (for the spinner)
  const [loadingChannel, setLoadingChannel] = useState<'email' | 'sms' | 'whatsapp' | null>(null);
  const [error, setError] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [captchaId, setCaptchaId] = useState<number | null>(null);
  const [captchaImage, setCaptchaImage] = useState('');

  // Username lookup state
  // 'nocontact' = the account exists but has neither an email nor a mobile on file.
  const [lookupStatus, setLookupStatus] = useState<'idle' | 'checking' | 'found' | 'notfound' | 'nocontact'>('idle');
  const [fetchedEmail, setFetchedEmail] = useState('');
  const [fetchedMobile, setFetchedMobile] = useState('');

  // OTP reset flow. Every channel (Email / SMS / WhatsApp) now sends a one-time OTP.
  // Steps: 'request' (choose channel) -> 'otp' (enter + verify OTP) -> 'password' (set new password).
  const [otpStep, setOtpStep] = useState<'request' | 'otp' | 'password'>('request');
  const [submittedUsername, setSubmittedUsername] = useState('');
  const [submittedChannel, setSubmittedChannel] = useState<'email' | 'sms' | 'whatsapp'>('email');
  const [otpValue, setOtpValue] = useState('');
  const [otpVerifyLoading, setOtpVerifyLoading] = useState(false);
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [otpResetLoading, setOtpResetLoading] = useState(false);

  const { captcha, requestPasswordReset, lookupForgotUser, verifyResetOtp, resetPasswordByOtp } = useAuth();
  const navigate = useNavigate();
  const usernameInputRef = useRef<HTMLInputElement>(null);

  // Human-readable label for where the OTP was sent (used in the OTP screen copy).
  const channelLabel =
    submittedChannel === 'sms' ? 'SMS' : submittedChannel === 'whatsapp' ? 'WhatsApp' : 'email';

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    resetField,
    setValue,
  } = useForm<ResetPasswordForm>({
    resolver: yupResolver(resetPasswordSchema),
    defaultValues: {
      username: '',
      captcha: '',
    },
  });

  const usernameReg = register('username');

  useEffect(() => {
    getCaptcha();

    // Auto-focus username input
    const timer = setTimeout(() => {
      if (usernameInputRef.current) {
        usernameInputRef.current.focus();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const getCaptcha = async () => {
    try {
      const response: CaptchaData | null = await captcha();
      if (response !== null) {
        setCaptchaId(response.cptch_id);
        setCaptchaImage(response.cptch_img_tx);
      }
    } catch (error) {
      console.error('Error loading captcha:', error);
      setError('Failed to load captcha');
    }
  };

  // Look up the user by username and store their email + mobile.
  const handleUsernameLookup = async (rawUsername: string) => {
    const username = (rawUsername || '').trim();

    // Reset any previous lookup
    setFetchedEmail('');
    setFetchedMobile('');

    if (!username) {
      setLookupStatus('idle');
      return;
    }

    setLookupStatus('checking');
    setError('');

    try {
      const res = await lookupForgotUser(username);
      // The account is "found" as long as it exists and has at least one contact
      // method (email OR mobile). Mobile-only users can still reset via SMS/WhatsApp.
      if (res.exists && (res.email || res.mobile)) {
        setFetchedEmail(res.email || '');
        setFetchedMobile(res.mobile || '');
        setLookupStatus('found');
      } else if (res.exists) {
        // Account exists but there is nowhere to send an OTP.
        setLookupStatus('nocontact');
      } else {
        setLookupStatus('notfound');
      }
    } catch {
      setLookupStatus('notfound');
    }
  };

  // `channel` is the delivery method the user chose (Email / SMS / WhatsApp).
  const onSubmit = async (
    data: ResetPasswordForm,
    channel: 'email' | 'sms' | 'whatsapp' = 'email'
  ) => {
    // Guard: the user must be found before sending.
    if (lookupStatus !== 'found') {
      setError('Please enter a valid username first.');
      return;
    }
    // The chosen channel needs the matching contact detail on file.
    if (channel === 'email' && !fetchedEmail) {
      setError('No email address is registered for this account. Use SMS or WhatsApp instead.');
      return;
    }
    if ((channel === 'sms' || channel === 'whatsapp') && !fetchedMobile) {
      setError('No mobile number is registered for this account. Use Email instead.');
      return;
    }

    setIsLoading(true);
    setLoadingChannel(channel);
    setError('');

    try {
      const payload: ForgotPasswordPayload = {
        username: data.username,
        email: fetchedEmail,            // fetched from the username lookup
        mobileNumber: fetchedMobile,    // fetched from the username lookup
        whatsappNumber: fetchedMobile,  // WhatsApp uses the same number as mobile
        captcha: data.captcha,
        captcha_id: captchaId || 0,
        channel, // 'email' | 'sms' | 'whatsapp' - the button the user clicked
      };

      await requestPasswordReset(payload);

      // All channels now deliver an OTP -> move to the OTP entry step.
      setSubmittedUsername(data.username);
      setSubmittedChannel(channel);
      setOtpValue('');
      setOtpStep('otp');
      reset();
      setLookupStatus('idle');
    } catch (error: any) {
      setError(error.message || 'Password reset failed. Please try again.');
      getCaptcha();
      resetField('captcha');
    } finally {
      setIsLoading(false);
      setLoadingChannel(null);
    }
  };

  // Step 1: verify the OTP (without consuming it). On success, move to the set-password step.
  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!/^\d{6}$/.test(otpValue)) {
      setError('Enter the 6-digit OTP');
      return;
    }

    setOtpVerifyLoading(true);
    try {
      const ok = await verifyResetOtp(submittedUsername, otpValue);
      if (ok) {
        setOtpStep('password');
      } else {
        setError('Invalid OTP. Please try again');
      }
    } catch (err: any) {
      setError(err.message || 'OTP verification failed');
    } finally {
      setOtpVerifyLoading(false);
    }
  };

  // Step 2: set the new password using the verified OTP, then move to the login page.
  const handleOtpReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (newPwd.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (newPwd !== confirmPwd) {
      setError('Passwords do not match');
      return;
    }

    setOtpResetLoading(true);
    try {
      await resetPasswordByOtp(submittedUsername, otpValue, newPwd);
      setResetSuccess(true);
      // After a successful reset, send the user to the login page.
      setTimeout(() => navigate('/login'), 1800);
    } catch (err: any) {
      setError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setOtpResetLoading(false);
    }
  };

  const buttonsDisabled = isLoading || lookupStatus !== 'found';

  if (resetSuccess) {
    return (
      <AuthShell bandLabel="DTCP ERP · Reset password">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 sm:h-14 sm:w-14 bg-green-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg shadow-green-600/30">
            <i className="ri-checkbox-circle-line text-white text-2xl sm:text-3xl"></i>
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Password reset successful
          </h2>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-6">
            Your password has been updated. Redirecting you to the login page&hellip;
          </p>
          <Link
            to="/login"
            className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Back to login
          </Link>
        </div>
      </AuthShell>
    );
  }

  // ---- Step 1: enter + verify the OTP delivered over the chosen channel ----
  if (otpStep === 'otp') {
    return (
      <AuthShell bandLabel="DTCP ERP · Reset password">
        <div className="text-center mb-6 sm:mb-8">
          <div className="mx-auto h-16 w-16 sm:h-20 sm:w-20 flex items-center justify-center mb-3 sm:mb-4 rounded-full ring-1 ring-slate-200 dark:ring-gray-700 bg-slate-50 dark:bg-gray-900/40 lg:hidden">
            <img
              src="/images/header/ap-logo-large.png"
              alt="Andhra Pradesh Government"
              className="w-14 h-14 sm:w-16 sm:h-16 object-contain"
            />
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            Enter OTP
          </h2>
          <p className="mt-2 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
            We sent a 6-digit OTP via {channelLabel}. Enter it below to continue.
          </p>
        </div>

        <form onSubmit={handleVerifyOtp} className="space-y-4">
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
                  <div className="flex items-center">
                    <i className="ri-error-warning-line text-red-400 mr-2"></i>
                    <span className="text-sm text-red-700 dark:text-red-400">{error}</span>
                  </div>
                </div>
              )}

              <FormInput
                label="OTP"
                type="text"
                icon={<i className="ri-message-2-line"></i>}
                placeholder="Enter 6-digit OTP"
                value={otpValue}
                onChange={(e) => setOtpValue(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric"
                maxLength={6}
              />

              <button
                type="submit"
                disabled={otpVerifyLoading || otpValue.length !== 6}
                className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {otpVerifyLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Verifying OTP...
                  </div>
                ) : (
                  'Verify OTP'
                )}
              </button>

              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setOtpStep('request');
                    setOtpValue('');
                    setError('');
                    getCaptcha();
                  }}
                  className="inline-flex items-center text-sm text-gray-600 hover:text-gray-500 dark:text-gray-400 dark:hover:text-gray-300"
                >
                  <i className="ri-arrow-left-line mr-1"></i>
                  Back
                </button>
              </div>
        </form>
      </AuthShell>
    );
  }

  // ---- Step 2: set a new password (OTP already verified) ----
  if (otpStep === 'password') {
    return (
      <AuthShell bandLabel="DTCP ERP · Reset password">
        <div className="text-center mb-6 sm:mb-8">
          <div className="mx-auto h-16 w-16 sm:h-20 sm:w-20 flex items-center justify-center mb-3 sm:mb-4 rounded-full ring-1 ring-slate-200 dark:ring-gray-700 bg-slate-50 dark:bg-gray-900/40 lg:hidden">
            <img
              src="/images/header/ap-logo-large.png"
              alt="Andhra Pradesh Government"
              className="w-14 h-14 sm:w-16 sm:h-16 object-contain"
            />
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            Set a new password
          </h2>
          <p className="mt-2 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
            Your OTP is verified. Create a new password for your account.
          </p>
        </div>

        <form onSubmit={handleOtpReset} className="space-y-4">
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
                  <div className="flex items-center">
                    <i className="ri-error-warning-line text-red-400 mr-2"></i>
                    <span className="text-sm text-red-700 dark:text-red-400">{error}</span>
                  </div>
                </div>
              )}

              <FormInput
                label="New Password"
                type="password"
                icon={<i className="ri-lock-line"></i>}
                placeholder="Enter new password"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
              />

              <FormInput
                label="Confirm Password"
                type="password"
                icon={<i className="ri-lock-line"></i>}
                placeholder="Re-enter new password"
                value={confirmPwd}
                onChange={(e) => setConfirmPwd(e.target.value)}
              />

              <button
                type="submit"
                disabled={otpResetLoading}
                className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {otpResetLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Resetting password...
                  </div>
                ) : (
                  'Reset password'
                )}
              </button>

              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setOtpStep('otp');
                    setNewPwd('');
                    setConfirmPwd('');
                    setError('');
                  }}
                  className="inline-flex items-center text-sm text-gray-600 hover:text-gray-500 dark:text-gray-400 dark:hover:text-gray-300"
                >
                  <i className="ri-arrow-left-line mr-1"></i>
                  Back
                </button>
              </div>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell bandLabel="DTCP ERP · Reset password">
      {/* Header */}
      <div className="text-center mb-6 sm:mb-8">
        <div className="mx-auto h-16 w-16 sm:h-20 sm:w-20 flex items-center justify-center mb-3 sm:mb-4 rounded-full ring-1 ring-slate-200 dark:ring-gray-700 bg-slate-50 dark:bg-gray-900/40 lg:hidden">
          <img
            src="/images/header/ap-logo-large.png"
            alt="Andhra Pradesh Government"
            className="w-14 h-14 sm:w-16 sm:h-16 object-contain"
          />
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
              Forgot password?
            </h2>
            <p className="mt-2 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
              Enter your username and choose how to receive the OTP
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit((data) => onSubmit(data, 'email'))} className="space-y-4">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
                <div className="flex items-center">
                  <i className="ri-error-warning-line text-red-400 mr-2"></i>
                  <span className="text-sm text-red-700 dark:text-red-400">{error}</span>
                </div>
              </div>
            )}

            {/* Wrong-username alert shown ABOVE the username field */}
            {lookupStatus === 'notfound' && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
                <div className="flex items-center">
                  <i className="ri-error-warning-line text-red-400 mr-2"></i>
                  <span className="text-sm text-red-700 dark:text-red-400">
                    No active account found for this username.
                  </span>
                </div>
              </div>
            )}

            {/* Account exists but has neither email nor mobile - no channel can deliver the OTP */}
            {lookupStatus === 'nocontact' && (
              <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-300 dark:border-orange-800 rounded-md p-3">
                <div className="flex items-start">
                  <i className="ri-alert-line text-orange-500 mr-2 mt-0.5"></i>
                  <span className="text-sm text-orange-800 dark:text-orange-300">
                    You don't have a mobile number or email registered. Please contact your administrator to reset your password.
                  </span>
                </div>
              </div>
            )}

            <FormInput
              label="Username"
              type="text"
              icon={<i className="ri-user-line"></i>}
              error={errors.username?.message}
              placeholder="Enter your username"
              success={lookupStatus === 'found'}
              rightAdornment={
                lookupStatus === 'checking' ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                ) : lookupStatus === 'found' ? (
                  <i className="ri-checkbox-circle-fill text-green-500 text-lg"></i>
                ) : null
              }
              {...usernameReg}
              onBlur={(e) => {
                usernameReg.onBlur(e);
                handleUsernameLookup(e.target.value);
              }}
              ref={(e) => {
                usernameReg.ref(e);
                usernameInputRef.current = e;
              }}
            />

            {/* Captcha Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Captcha Verification
                </label>
                <button
                  type="button"
                  onClick={getCaptcha}
                  className="text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 text-sm"
                >
                  <i className="ri-refresh-line mr-1"></i>
                  Refresh
                </button>
              </div>

              <div className="flex items-start gap-3">
                {/* Captcha Image */}
                <div className="flex-shrink-0 w-40">
                  {captchaImage ? (
                    <img
                      src={captchaImage}
                      alt="Captcha"
                      className="w-full h-12 object-contain border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700"
                      onError={() => {
                        console.error('Captcha image failed to load');
                      }}
                    />
                  ) : (
                    <Skeleton shape="rounded" className="h-12 w-full" />
                  )}
                </div>

                {/* Input Field */}
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder="Enter answer"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white h-12"
                    maxLength={4}
                    pattern="[0-9]*"
                    inputMode="numeric"
                    style={{ letterSpacing: '0.5em' }}
                    {...register('captcha')}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                      e.target.value = value;
                      setValue('captcha', value);
                    }}
                  />
                  {errors.captcha && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                      {errors.captcha.message}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Delivery channel buttons - user picks how to receive the reset link.
                The chosen channel ('email' | 'sms' | 'whatsapp') is passed into onSubmit,
                along with the email/mobile fetched from the username lookup. */}
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400 text-center">Send OTP via</p>
            <div className="flex items-center justify-center gap-3">
              <button
                type="button"
                disabled={buttonsDisabled || !fetchedEmail}
                title={fetchedEmail ? 'Email' : 'No email on file for this account'}
                aria-label="Send reset link via Email"
                onClick={handleSubmit((data) => onSubmit(data, 'email'))}
                className="flex items-center justify-center h-9 w-9 border border-transparent rounded-full shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loadingChannel === 'email' ? (
                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
                ) : (
                  <i className="ri-mail-line text-base"></i>
                )}
              </button>

              <button
                type="button"
                disabled={buttonsDisabled || !fetchedMobile}
                title={fetchedMobile ? 'SMS' : 'No mobile number on file for this account'}
                aria-label="Send reset OTP via SMS"
                onClick={handleSubmit((data) => onSubmit(data, 'sms'))}
                className="flex items-center justify-center h-9 w-9 border border-transparent rounded-full shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loadingChannel === 'sms' ? (
                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
                ) : (
                  <i className="ri-message-2-line text-base"></i>
                )}
              </button>

              <button
                type="button"
                disabled={buttonsDisabled || !fetchedMobile}
                title={fetchedMobile ? 'WhatsApp' : 'No mobile number on file for this account'}
                aria-label="Send reset link via WhatsApp"
                onClick={handleSubmit((data) => onSubmit(data, 'whatsapp'))}
                className="flex items-center justify-center h-9 w-9 border border-transparent rounded-full shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loadingChannel === 'whatsapp' ? (
                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
                ) : (
                  <i className="ri-whatsapp-line text-base"></i>
                )}
              </button>
            </div>

            {/* OLD single button - kept (commented out) per request, replaced by the channel buttons above
            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Sending reset link...
                </div>
              ) : (
                'Send reset link'
              )}
            </button>
            */}

            <div className="text-center pt-2">
              <Link
                to="/login"
                className="inline-flex items-center text-sm text-gray-600 hover:text-gray-500 dark:text-gray-400 dark:hover:text-gray-300"
              >
                <i className="ri-arrow-left-line mr-1"></i>
                Back to login
              </Link>
            </div>
      </form>
    </AuthShell>
  );
};

export default ForgotPassword;
